import t, { plural } from "@repo/localization/string.macro";
import { Promotion as CanadaPromotionPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/promotions/promotion_pb";
import { Promo as UsPromoPb } from "@repo/protobuf/gen/web/public/user_pb";
import { MessageFrom } from "@repo/protobuf/utils";
import Case from "@repo/utils/Case";
import Chainable from "@repo/utils/Chainable";
import Enum from "@repo/utils/Enum";
import Money from "@repo/utils/Money";
import UArray from "@repo/utils/UArray";

import Subscription, { SubscriptionCanada } from "./Subscription";

type Promotion = {
  id: Promotion.Id;
  amount: number;
  unit: Promotion.Unit;
  monthsLeft: number;
  validTiers: readonly Subscription.Tier[];
  forState: {
    subscriptionRenewal?: boolean;
  };
};

namespace Promotion {
  export type Id = string & {};

  export type Unit = "cents" | "percent";

  export const description = Chainable.create(
    Chainable.wrapFactory(
      ({ tier = null as null | Subscription.Tier }) =>
        (promotion: Promotion | undefined): string | undefined => {
          if (!promotion) return;
          if (tier && !promotion?.validTiers.includes(tier)) return;

          const value = (() => {
            if (promotion.unit === "cents") {
              return Money.format(promotion.amount);
            } else if (promotion.unit === "percent") {
              return promotion.amount.toString() + "%";
            }
          })();

          if (tier || UArray.equal(promotion.validTiers, Subscription.tiers))
            return t`${value} off for ${promotion.monthsLeft} ${plural(
              promotion.monthsLeft,
              {
                one: "month",
                other: "months",
              }
            )}`;

          const plans = new Intl.ListFormat("en", {
            style: "long",
            type: "conjunction",
          }).format(promotion.validTiers.map(Subscription.Tier.format));

          return t`${value} off ${plans} for ${promotion.monthsLeft} ${plural(
            promotion.monthsLeft,
            {
              one: "month",
              other: "months",
            }
          )}`;
        }
    ),
    {
      forTier: (tier: Subscription.Tier) => ({
        tier,
      }),
    }
  );

  export function discountedValue(
    cents: number,
    promotion: Promotion
  ): number | undefined {
    let discountedCents = cents;
    if (promotion.unit === "cents") {
      discountedCents = cents - promotion.amount;
    } else if (promotion.unit === "percent") {
      discountedCents = cents - cents * (promotion.amount / 100);
    }
    return discountedCents;
  }
}

export default Promotion;

export namespace PromotionUsa {
  export function normalize(promotion: MessageFrom<UsPromoPb>): Promotion {
    return {
      id: promotion.name,
      amount: promotion.monthlyCreditAmountCents,
      unit: "cents",
      monthsLeft: promotion.monthsCredited,
      // TODO: Filter tiers properly
      validTiers: Subscription.tiers,
      forState: {
        subscriptionRenewal: promotion.name.includes("subscription_renewal"),
      },
    };
  }
}

export namespace PromotionCanada {
  export function normalize(
    promotion: MessageFrom<CanadaPromotionPb>
  ): Promotion {
    const unit = Case.fromConstant(
      Enum.keyOf(CanadaPromotionPb.Unit, promotion.unit)
    ).toCamel();

    if (unit === "unknown")
      throw new Error("Unexpected UNKNOWN unit in promotion.");

    return {
      // TODO
      id: `${promotion.amount}-${unit}-${promotion.validTiers.join(",")}`,
      unit: unit,
      amount: promotion.amount,
      monthsLeft: promotion.monthsLeft,
      validTiers: promotion.validTiers.map(
        SubscriptionCanada.Plan.Tier.normalize
      ),
      forState: {},
    };
  }
}
